import { useEffect, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { STORE_STATISTICS_MUTATION } from '../../payment/graphql/mutations';
import {
  NEW_OFFER_RATE_SOLUTION,
  OFFERED_PRODUCTS,
  USED_OFFER_RATE_SOLUTION,
} from '../constants/formFields';

const uniqNumbers = (rawIds: (number | string)[]): number[] =>
  Array.from(new Set(rawIds.map(Number).filter((id) => !Number.isNaN(id))));

const getProductsFromDOM = (): number[] => {
  const elements = document.querySelectorAll('[data-offer-rate]');
  const ids = Array.from(elements, (el) => el.getAttribute('data-offer-rate') as string);
  return uniqNumbers(ids);
};

const getProductsFromSession = (): number[] => {
  try {
    return JSON.parse(sessionStorage.getItem(OFFERED_PRODUCTS) || '[]');
  } catch {
    return [];
  }
};

const storeProducts = (productIds: number[]) => {
  const sortedProductIds = [...productIds].sort((a, b) => a - b);
  sessionStorage.setItem(OFFERED_PRODUCTS, JSON.stringify(sortedProductIds));
  sessionStorage.setItem(USED_OFFER_RATE_SOLUTION, NEW_OFFER_RATE_SOLUTION);
};

const useProductsOfferRate = (
  extraProducts: any[],
  stepIndex: number | null,
  shouldCallStoreStatistic: boolean = true,
) => {
  const [storeStatistics] = useMutation(STORE_STATISTICS_MUTATION);

  const sendStatisticsIf = useCallback(
    (offeredProductsToSend: number[]) => {
      storeStatistics({
        variables: {
          brandedFareOffered: sessionStorage.getItem('brandedFareOffered') === 'true',
          offeredProducts: offeredProductsToSend.map(String),
          paymentRequest: false,
          stepIndex,
        },
      });
    },
    [storeStatistics, stepIndex],
  );

  useEffect(() => {
    if (!shouldCallStoreStatistic) {
      return;
    }
    const productsInTheDom = getProductsFromDOM();
    const prevOfferedProducts = getProductsFromSession();

    const offeredProductsToSend = productsInTheDom.filter(
      (id) => !prevOfferedProducts.includes(id),
    );

    if (!offeredProductsToSend.length) {
      return;
    }

    sendStatisticsIf(offeredProductsToSend);

    const combinedOfferedProducts = uniqNumbers([...prevOfferedProducts, ...productsInTheDom]);
    storeProducts(combinedOfferedProducts);
  }, [sendStatisticsIf, extraProducts, shouldCallStoreStatistic]);
};

export default useProductsOfferRate;
